import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Loader2, FileText, Upload, Mail, User, Award, Download } from 'lucide-react';
import { saveStudentDocument, getAllStudentDocuments, deleteStudentDocument } from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import { getBatchEnrollments, getBatches } from '../../firebase/collections';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const ResultsAdmitCardManagement = () => {
  const { userProfile } = useRealtime();
  const [documents, setDocuments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [documentType, setDocumentType] = useState('result'); // 'result' or 'admitCard'
  const [formData, setFormData] = useState({
    studentEmail: '',
    studentId: '',
    studentName: '',
    examName: '',
    examDate: '',
    documentUrl: '',
    batchId: ''
  });

  const isSuperAdmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin' || userProfile?.role === 'founder';
  const coachingId = isSuperAdmin ? null : (userProfile?.coachingId || null);

  useEffect(() => {
    loadDocuments();
    loadBatches();
  }, [documentType]);

  useEffect(() => {
    if (formData.batchId) {
      loadEnrolledStudents(formData.batchId);
    } else {
      setEnrolledStudents([]);
    }
  }, [formData.batchId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const filters = coachingId ? { coachingId } : {};
      const result = await getAllStudentDocuments(documentType, filters);
      if (result.success) {
        setDocuments(result.data || []);
      } else {
        toast.error('Failed to load documents');
      }
    } catch (error) {
      toast.error('Error loading documents');
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    try {
      const result = await getBatches(coachingId ? { coachingId } : {});
      if (result.success) {
        setBatches(result.data || []);
      }
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  const loadEnrolledStudents = async (batchId) => {
    try {
      const result = await getBatchEnrollments(batchId);
      if (result.success) {
        setEnrolledStudents(result.data || []);
      }
    } catch (error) {
      console.error('Error loading enrolled students:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If student selected from list, auto-fill email and name
    if (field === 'studentId' && value) {
      const student = enrolledStudents.find(s => s.studentId === value || s.id === value);
      if (student) {
        setFormData(prev => ({
          ...prev,
          studentEmail: student.studentEmail || student.email || '',
          studentName: student.studentName || student.name || '',
          studentId: student.studentId || student.id
        }));
      }
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF or image file');
      return;
    }

    try {
      setUploading(true);
      const fileName = `${documentType}s/${Date.now()}_${file.name}`;
      const result = await uploadFile(file, fileName);
      
      if (result.success) {
        setFormData(prev => ({ ...prev, documentUrl: result.url }));
        toast.success('File uploaded successfully');
      } else {
        toast.error('Failed to upload file: ' + result.error);
      }
    } catch (error) {
      toast.error('Error uploading file: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (doc) => {
    setFormData({
      studentEmail: doc.studentEmail || '',
      studentId: doc.studentId || '',
      studentName: doc.studentName || '',
      examName: doc.examName || '',
      examDate: doc.examDate || '',
      documentUrl: doc.documentUrl || '',
      batchId: doc.batchId || ''
    });
    setEditingId(doc.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({
      studentEmail: '',
      studentId: '',
      studentName: '',
      examName: '',
      examDate: '',
      documentUrl: '',
      batchId: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!formData.studentEmail.trim()) {
      toast.error('Please enter student email or select a student');
      return;
    }
    if (!formData.documentUrl) {
      toast.error('Please upload a document');
      return;
    }

    try {
      setSaving(true);
      const result = editingId
        ? await saveStudentDocument({ ...formData, id: editingId, documentType, coachingId })
        : await saveStudentDocument({ ...formData, documentType, coachingId });

      if (result.success) {
        toast.success(editingId ? 'Document updated successfully' : 'Document added successfully');
        handleCancel();
        loadDocuments();
      } else {
        toast.error('Failed to save: ' + result.error);
      }
    } catch (error) {
      toast.error('Error saving document: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const result = await deleteStudentDocument(id, documentType);
      if (result.success) {
        toast.success('Document deleted successfully');
        loadDocuments();
      } else {
        toast.error('Failed to delete: ' + result.error);
      }
    } catch (error) {
      toast.error('Error deleting document: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {documentType === 'result' ? 'Results Management' : 'Admit Card Management'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage student {documentType === 'result' ? 'results/marksheets' : 'admit cards'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => {
                setDocumentType('result');
                setDocuments([]);
                loadDocuments();
              }}
              className={`px-4 py-2 rounded ${documentType === 'result' ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
            >
              Results
            </button>
            <button
              onClick={() => {
                setDocumentType('admitCard');
                setDocuments([]);
                loadDocuments();
              }}
              className={`px-4 py-2 rounded ${documentType === 'admitCard' ? 'bg-blue-600 text-white' : 'text-gray-700 dark:text-gray-300'}`}
            >
              Admit Cards
            </button>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add {documentType === 'result' ? 'Result' : 'Admit Card'}
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingId ? `Edit ${documentType === 'result' ? 'Result' : 'Admit Card'}` : `Add New ${documentType === 'result' ? 'Result' : 'Admit Card'}`}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Batch (Optional)
              </label>
              <select
                value={formData.batchId}
                onChange={(e) => handleInputChange('batchId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">None - Enter email manually</option>
                {batches.map(batch => (
                  <option key={batch.id} value={batch.id}>{batch.name}</option>
                ))}
              </select>
            </div>

            {formData.batchId && enrolledStudents.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Student from Batch
                </label>
                <select
                  value={formData.studentId}
                  onChange={(e) => handleInputChange('studentId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select a student</option>
                  {enrolledStudents.map(student => (
                    <option key={student.id || student.studentId} value={student.studentId || student.id}>
                      {student.studentName || student.name} ({student.studentEmail || student.email})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Student Email *
              </label>
              <input
                type="email"
                value={formData.studentEmail}
                onChange={(e) => handleInputChange('studentEmail', e.target.value.toLowerCase())}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="student@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Student Name
              </label>
              <input
                type="text"
                value={formData.studentName}
                onChange={(e) => handleInputChange('studentName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Student full name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {documentType === 'result' ? 'Exam Name' : 'Exam Name'}
                </label>
                <input
                  type="text"
                  value={formData.examName}
                  onChange={(e) => handleInputChange('examName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., JEE Main 2025"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Exam Date
                </label>
                <input
                  type="date"
                  value={formData.examDate}
                  onChange={(e) => handleInputChange('examDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document File * (PDF or Image)
              </label>
              {formData.documentUrl ? (
                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <FileText className="w-8 h-8 text-blue-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">File Uploaded</p>
                    <a
                      href={formData.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View Document
                    </a>
                  </div>
                  <button
                    onClick={() => handleInputChange('documentUrl', '')}
                    className="text-red-600 hover:text-red-800"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  {uploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span>Upload PDF or Image (Max 10MB)</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleFileUpload(e.target.files[0]);
                      }
                    }}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Documents List */}
      {documents.length === 0 && !showForm ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          {documentType === 'result' ? (
            <Award className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          ) : (
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          )}
          <p className="text-gray-600 dark:text-gray-400">No {documentType === 'result' ? 'results' : 'admit cards'} yet.</p>
        </div>
      ) : (
        !showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Student</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Exam</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {documents.map((doc) => (
                    <tr key={doc.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {doc.studentName || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">{doc.studentEmail}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {doc.examName || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                        {doc.examDate || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <a
                            href={doc.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                            title="View"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            onClick={() => handleEdit(doc)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(doc.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default ResultsAdmitCardManagement;

